import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, Mail } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { StatusBadge } from "@/components/dashboard/status-badge";
import { LiveView } from "@/components/interviews/live-view";
import { Results } from "@/components/interviews/results";
import { CopyLink } from "@/components/interviews/copy-link";
import { StatusWatcher } from "@/components/interviews/status-watcher";
import { DecisionBadge, DecisionControl } from "@/components/interviews/decision";
import { initials } from "@/lib/utils";
import { useDict, fill } from "@/lib/i18n/context";
import type { Interview, InterviewQuestion } from "@/lib/types";
import { Badge } from "@/components/ui/badge";

type InterviewDetail = Interview & {
  candidate_name: string | null;
  candidate_email: string | null;
  job_title: string | null;
  agency_company_name?: string | null;
  client_company_name?: string | null;
};

type DetailData = {
  interview: InterviewDetail;
  questions: InterviewQuestion[];
  inviteLink: string;
  canDecide?: boolean;
  isPartneredView?: boolean;
};

export function InterviewDetailPage() {
  const { id } = useParams<{ id: string }>();
  const t = useDict();
  const [data, setData] = useState<DetailData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    setNotFound(false);
    fetch(`/api/interviews/${id}`, { credentials: "include" })
      .then(async (res) => {
        if (res.status === 404) {
          setNotFound(true);
          return null;
        }
        if (!res.ok) throw new Error("Failed to load");
        return res.json() as Promise<DetailData>;
      })
      .then((json) => {
        // #region agent log
        fetch("http://127.0.0.1:7892/ingest/b0e9c3ac-9fe9-40e4-b61a-a0051cd95f6a", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Debug-Session-Id": "629ce6",
          },
          body: JSON.stringify({
            sessionId: "629ce6",
            runId: "white-screen",
            hypothesisId: "H5",
            location: "InterviewDetailPage.tsx:load",
            message: "interview detail loaded",
            data: {
              id,
              status: json?.interview?.status ?? null,
              hasQuestions: Boolean(json?.questions?.length),
            },
            timestamp: Date.now(),
          }),
        }).catch(() => {});
        // #endregion
        if (json) setData(json);
      })
      .catch(() => setError("Failed to load"))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    // #region agent log
    const onErr = (event: ErrorEvent) => {
      fetch("http://127.0.0.1:7892/ingest/b0e9c3ac-9fe9-40e4-b61a-a0051cd95f6a", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Debug-Session-Id": "629ce6",
        },
        body: JSON.stringify({
          sessionId: "629ce6",
          runId: "white-screen",
          hypothesisId: "H1",
          location: "InterviewDetailPage.tsx:window.onerror",
          message: "window error",
          data: {
            error: String(event.error?.message || event.message || ""),
            stack: String(event.error?.stack || "").slice(0, 400),
          },
          timestamp: Date.now(),
        }),
      }).catch(() => {});
    };
    window.addEventListener("error", onErr);
    return () => window.removeEventListener("error", onErr);
    // #endregion
  }, []);

  if (loading && !data) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-muted">
        Loading…
      </div>
    );
  }
  if (notFound) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-muted">
        Not found
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-muted">
        {error || "Failed to load"}
      </div>
    );
  }

  const { interview, questions, inviteLink, canDecide = true, isPartneredView } =
    data;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <Link
        to="/interviews"
        className="inline-flex items-center gap-1 text-sm text-muted hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4 rtl:rotate-180" /> {t.interviews.backToInterviews}
      </Link>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-100 text-lg font-semibold text-brand-700">
            {initials(interview.candidate_name ?? "?")}
          </span>
          <div>
            <h1 className="text-2xl font-bold">
              {interview.candidate_name ?? t.common.candidate}
            </h1>
            <p className="flex flex-wrap items-center gap-3 text-sm text-muted">
              <span className="flex items-center gap-1">
                <Mail className="h-3.5 w-3.5" /> {interview.candidate_email}
              </span>
              <span>{interview.job_title ?? t.common.generalInterview}</span>
              {isPartneredView && interview.agency_company_name && (
                <Badge tone="brand">
                  {fill(t.partners.viaAgency, {
                    agency: interview.agency_company_name,
                  })}
                </Badge>
              )}
              {!isPartneredView &&
                interview.partnership_id &&
                interview.client_company_name && (
                  <Badge tone="brand">
                    {fill(t.partners.forClient, {
                      client: interview.client_company_name,
                    })}
                  </Badge>
                )}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {(interview.violation_count ?? 0) > 0 && (
            <Badge tone="amber">
              {fill(t.results.integrityTimes, {
                count: String(interview.violation_count),
              })}{" "}
              · {t.results.integrityLeft}
            </Badge>
          )}
          <DecisionBadge decision={interview.decision} />
          <StatusBadge status={interview.status} />
        </div>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-6">
          <div>
            <p className="font-medium">{t.interviews.decisionTitle}</p>
            <p className="text-sm text-muted">
              {canDecide
                ? t.interviews.decisionHint
                : t.partners.clientDecides}
            </p>
          </div>
          {canDecide ? (
            <DecisionControl interviewId={interview.id} current={interview.decision} />
          ) : (
            <DecisionBadge decision={interview.decision} />
          )}
        </CardContent>
      </Card>

      {interview.status === "invited" && (
        <Card>
          <StatusWatcher interviewId={interview.id} currentStatus={interview.status} />
          <CardContent className="space-y-4 pt-6 text-center">
            <Clock className="mx-auto h-10 w-10 text-muted/60" />
            <div>
              <p className="font-medium">{t.interviews.waitingTitle}</p>
              <p className="text-sm text-muted">
                {interview.scheduled_at
                  ? `${t.interviews.waitingScheduled} ${new Date(interview.scheduled_at).toLocaleString()}`
                  : t.interviews.waitingNoSchedule}
              </p>
            </div>
            <div className="mx-auto max-w-lg text-start">
              <p className="mb-1 text-sm font-medium">{t.interviews.interviewLink}</p>
              <CopyLink link={inviteLink} />
            </div>
          </CardContent>
        </Card>
      )}

      {interview.status === "in_progress" && (
        <LiveView interviewId={interview.id} totalQuestions={questions.length} />
      )}

      {(interview.status === "completed" || interview.status === "violated") && (
        <Results interview={interview} questions={questions} />
      )}
    </div>
  );
}
